import React, { useEffect } from 'react'

import {
  pantryColors,
  pantryRadii,
  pantryShadows,
} from '../../../foundations/tokens'

export interface ModalProps {
  open: boolean
  title?: string
  subtitle?: string
  onClose: () => void
  footer?: React.ReactNode
  children?: React.ReactNode
  width?: number | string
  zIndex?: number
}

export const Modal: React.FC<ModalProps> = ({
  open,
  title,
  subtitle,
  onClose,
  footer,
  children,
  width = 480,
  zIndex,
}) => {
  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    if (open) document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [open, onClose])

  if (!open) return null

  return (
    <>
      <div
        aria-hidden="true"
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: zIndex != null ? zIndex : 40,
          background: 'rgba(17, 24, 39, 0.4)',
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title ?? 'Dialog'}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: zIndex != null ? zIndex + 1 : 50,
          display: 'grid',
          placeItems: 'center',
          padding: 24,
        }}
      >
        <div
          style={{
            width,
            maxWidth: '100%',
            background: pantryColors.white,
            borderRadius: pantryRadii.lg,
            boxShadow: pantryShadows.lg,
            overflow: 'hidden',
          }}
        >
          {title && (
            <div
              style={{
                padding: '20px 24px 16px',
                borderBottom: `1px solid ${pantryColors.gray[200]}`,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 17,
                    fontWeight: 700,
                    color: pantryColors.gray[800],
                  }}
                >
                  {title}
                </div>
                {subtitle && (
                  <div
                    style={{
                      marginTop: 4,
                      fontSize: 12,
                      color: pantryColors.gray[400],
                    }}
                  >
                    {subtitle}
                  </div>
                )}
              </div>
              <button
                type="button"
                aria-label="Close"
                onClick={onClose}
                style={{
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  color: pantryColors.gray[400],
                  padding: 4,
                  margin: '-4px -4px 0 0',
                  borderRadius: 6,
                  display: 'flex',
                  alignItems: 'center',
                  flexShrink: 0,
                }}
              >
                <svg
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  viewBox="0 0 24 24"
                  width={16}
                  height={16}
                  aria-hidden="true"
                >
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}
          <div style={{ padding: 24 }}>{children}</div>
          {footer && (
            <div
              style={{
                padding: '16px 24px',
                borderTop: `1px solid ${pantryColors.gray[200]}`,
                background: pantryColors.gray[50],
              }}
            >
              {footer}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
